import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  Plus,
  Minus,
  Leaf,
  Drumstick,
  CheckCircle2,
  X,
  ShoppingBag,
  Star,
} from "lucide-react";

const resolveBackendUrl = () => {
  const prod = process.env.REACT_APP_BACKEND_URL;
  const local = process.env.REACT_APP_BACKEND_URL_LOCAL;
  const forceLocal = process.env.REACT_APP_USE_LOCAL_BACKEND === "true";
  const isDev = process.env.NODE_ENV === "development";
  return (forceLocal || isDev) && local ? local : prod;
};

const API_BASE = `${resolveBackendUrl()}/api`;
const pub = axios.create({ baseURL: API_BASE });

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const VegBadge = ({ type }) => {
  const color =
    type === "veg" ? "#6A7F66" : type === "non-veg" ? "#D95D39" : "#E9C46A";
  return (
    <div
      className="w-4 h-4 border-2 flex items-center justify-center rounded-sm"
      style={{ borderColor: color }}
      title={type}
    >
      <div
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
    </div>
  );
};

export default function PublicOrder() {
  const { outletId, tableId } = useParams();
  const [loading, setLoading] = useState(true);
  const [outlet, setOutlet] = useState(null);
  const [tableInfo, setTableInfo] = useState(null);
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({}); // {menu_item_id: {item, qty}}
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [o, m] = await Promise.all([
          pub.get(`/public/outlet/${outletId}`),
          pub.get(`/public/menu/${outletId}`),
        ]);
        setOutlet(o.data);
        setCats(m.data.categories || []);
        setItems(m.data.items || []);
        if (tableId) {
          try {
            const t = await pub.get(`/public/table/${outletId}/${tableId}`);
            setTableInfo(t.data);
          } catch {
            /* table optional */
          }
        }
      } catch (e) {
        // ignore – outlet not found state handled in render
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [outletId, tableId]);

  const filtered = useMemo(() => {
    let list = items.filter((i) => i.active);
    if (activeCat !== "all")
      list = list.filter((i) => i.category_id === activeCat);
    const s = search.trim().toLowerCase();
    if (s)
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(s) ||
          (i.code || "").toLowerCase().includes(s),
      );
    return list;
  }, [items, activeCat, search]);

  const add = (mi) =>
    setCart((c) => ({
      ...c,
      [mi.id]: { item: mi, qty: (c[mi.id]?.qty || 0) + 1 },
    }));
  const sub = (mi) =>
    setCart((c) => {
      const cur = c[mi.id]?.qty || 0;
      if (cur <= 1) {
        const { [mi.id]: _, ...rest } = c;
        return rest;
      }
      return { ...c, [mi.id]: { item: mi, qty: cur - 1 } };
    });

  const cartArr = Object.values(cart);
  const totalQty = cartArr.reduce((a, c) => a + c.qty, 0);
  const subtotal = cartArr.reduce((a, c) => a + c.qty * (c.item.price || 0), 0);

  const placeOrder = async () => {
    if (cartArr.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        outlet_id: outletId,
        table_id: tableId || null,
        customer_name: customer.name,
        customer_phone: customer.phone,
        notes,
        items: cartArr.map((c) => ({
          menu_item_id: c.item.id,
          name: c.item.name,
          price: c.item.price,
          qty: c.qty,
          gst_rate: c.item.gst_rate || 5,
          modifiers: [],
          notes: "",
        })),
      };
      const { data } = await pub.post("/public/orders", payload);
      setSuccess(data);
      setCart({});
      setDrawer(false);
    } catch (e) {
      alert(
        e?.response?.data?.detail || "Could not place order. Please ask staff.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFAF8]">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#EBE8E2] border-t-[#D95D39] rounded-full animate-spin mx-auto mb-3" />
          <div className="text-sm text-[#7A7873]">Loading menu…</div>
        </div>
      </div>
    );
  }

  if (!outlet) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFAF8] p-6">
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-8 text-center max-w-sm">
          <div className="font-heading font-bold text-xl text-[#2D2C2A] mb-2">
            Restaurant not found
          </div>
          <p className="text-sm text-[#7A7873]">
            This QR code may be invalid or the restaurant is closed.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FBFAF8] p-6"
        data-testid="public-order-success"
      >
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-8 text-center max-w-sm w-full">
          <CheckCircle2 size={56} className="mx-auto text-[#6A7F66] mb-3" />
          <div className="font-heading font-extrabold text-2xl text-[#2D2C2A]">
            Order Placed!
          </div>
          <div className="text-sm text-[#7A7873] mt-1">
            Waiter has been notified.
          </div>
          <div className="bg-[#FBFAF8] border border-[#EBE8E2] rounded-md p-3 mt-4 text-left">
            <div className="text-xs text-[#7A7873]">Order Number</div>
            <div className="font-mono-num font-bold text-lg text-[#2D2C2A]">
              {success.order_no}
            </div>
            {success.token_no && (
              <>
                <div className="text-xs text-[#7A7873] mt-2">Token</div>
                <div className="font-mono-num font-bold text-lg text-[#D95D39]">
                  {success.token_no}
                </div>
              </>
            )}
            <div className="text-xs text-[#7A7873] mt-2">Estimated Total</div>
            <div className="font-mono-num font-bold text-lg text-[#2D2C2A]">
              {inr(success.total)}
            </div>
          </div>
          <Button
            className="mt-4 w-full bg-[#D95D39] hover:bg-[#C24F2C]"
            onClick={() => setSuccess(null)}
          >
            Order More
          </Button>
          <Button
            variant="outline"
            className="mt-2 w-full border-[#E9A23B] text-[#E9A23B] hover:bg-[#FFF4E0]"
            onClick={() => {
              window.location.href = `/feedback/${outletId}?order=${success.id}`;
            }}
            data-testid="leave-feedback-btn"
          >
            <Star size={14} className="mr-1.5" /> Leave Feedback
          </Button>
          <p className="text-[11px] text-[#7A7873] mt-3">
            Payment will be collected by staff after your meal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#FBFAF8] pb-28"
      data-testid="public-order-page"
    >
      <header className="bg-white border-b border-[#EBE8E2] sticky top-0 z-20">
        <div className="max-w-2xl mx-auto p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading font-extrabold text-lg text-[#2D2C2A]">
                {outlet.name}
              </h1>
              <p className="text-xs text-[#7A7873]">
                {tableInfo
                  ? `Table ${tableInfo.name} · Self Order`
                  : "Self Order"}
              </p>
            </div>
            {totalQty > 0 && (
              <Button
                size="sm"
                className="bg-[#D95D39] hover:bg-[#C24F2C]"
                onClick={() => setDrawer(true)}
              >
                <ShoppingBag size={14} className="mr-1.5" /> {totalQty} ·{" "}
                {inr(subtotal)}
              </Button>
            )}
          </div>
          <div className="relative mt-3">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7873]"
              size={16}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dish or code…"
              className="pl-9 h-10 border-[#EBE8E2] bg-[#FBFAF8]"
              data-testid="public-search"
            />
          </div>
          <Tabs value={activeCat} onValueChange={setActiveCat} className="mt-3">
            <TabsList className="bg-transparent p-0 h-auto flex-wrap justify-start gap-1">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39] text-xs"
              >
                All
              </TabsTrigger>
              {cats.map((c) => (
                <TabsTrigger
                  key={c.id}
                  value={c.id}
                  className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39] text-xs"
                >
                  {c.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-3">
        {filtered.map((mi) => {
          const qty = cart[mi.id]?.qty || 0;
          return (
            <div
              key={mi.id}
              className="bg-white border border-[#EBE8E2] rounded-lg p-3 flex gap-3"
              data-testid={`pub-item-${mi.id}`}
            >
              <div className="w-20 h-20 rounded-md bg-[#F4F2EE] flex items-center justify-center text-[#7A7873] shrink-0 overflow-hidden">
                {mi.image_url ? (
                  <img
                    src={mi.image_url}
                    alt={mi.name}
                    className="w-full h-full object-cover"
                  />
                ) : mi.type === "veg" ? (
                  <Leaf size={24} />
                ) : (
                  <Drumstick size={24} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <VegBadge type={mi.type} />
                  <div className="font-semibold text-sm text-[#2D2C2A] line-clamp-2">
                    {mi.name}
                  </div>
                </div>
                {mi.description && (
                  <div className="text-xs text-[#7A7873] mt-0.5 line-clamp-2">
                    {mi.description}
                  </div>
                )}
                <div className="flex items-center justify-between mt-1.5">
                  <div className="font-mono-num font-bold text-[#D95D39]">
                    {inr(mi.price)}
                  </div>
                  {qty === 0 ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 border-[#D95D39] text-[#D95D39]"
                      onClick={() => add(mi)}
                      data-testid={`pub-add-${mi.id}`}
                    >
                      <Plus size={12} className="mr-1" /> Add
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 bg-[#FBE9E2] rounded-md px-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-[#D95D39]"
                        onClick={() => sub(mi)}
                      >
                        <Minus size={12} />
                      </Button>
                      <div className="font-mono-num font-bold text-[#D95D39] w-5 text-center">
                        {qty}
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-[#D95D39]"
                        onClick={() => add(mi)}
                      >
                        <Plus size={12} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-[#7A7873] py-12 text-sm">
            No dishes match your search.
          </div>
        )}
      </main>

      {/* Floating cart bar */}
      {totalQty > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#EBE8E2] p-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)] z-20">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div>
              <div className="text-xs text-[#7A7873]">
                {totalQty} items added
              </div>
              <div className="font-mono-num font-extrabold text-lg text-[#2D2C2A]">
                {inr(subtotal)}
              </div>
            </div>
            <Button
              className="bg-[#D95D39] hover:bg-[#C24F2C]"
              onClick={() => setDrawer(true)}
              data-testid="view-cart-btn"
            >
              Review &amp; Place Order
            </Button>
          </div>
        </div>
      )}

      {/* Cart drawer */}
      {drawer && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={() => setDrawer(false)}
        />
      )}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-white z-40 transition-transform duration-200 ${drawer ? "translate-x-0" : "translate-x-full"} flex flex-col`}
      >
        <div className="p-4 border-b border-[#EBE8E2] flex items-center justify-between">
          <div className="font-heading font-bold text-lg text-[#2D2C2A]">
            Your Order
          </div>
          <Button size="icon" variant="ghost" onClick={() => setDrawer(false)}>
            <X size={18} />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {cartArr.map((c) => (
            <div
              key={c.item.id}
              className="flex items-center justify-between border-b border-dashed border-[#EBE8E2] pb-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2D2C2A] truncate">
                  {c.item.name}
                </div>
                <div className="text-xs text-[#7A7873] font-mono-num">
                  {inr(c.item.price)} × {c.qty}
                </div>
              </div>
              <div className="flex items-center gap-2 bg-[#FBE9E2] rounded-md px-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-[#D95D39]"
                  onClick={() => sub(c.item)}
                >
                  <Minus size={12} />
                </Button>
                <div className="font-mono-num text-sm w-5 text-center">
                  {c.qty}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-[#D95D39]"
                  onClick={() => add(c.item)}
                >
                  <Plus size={12} />
                </Button>
              </div>
              <div className="ml-3 font-mono-num font-semibold text-sm w-16 text-right">
                {inr(c.item.price * c.qty)}
              </div>
            </div>
          ))}
          {cartArr.length === 0 && (
            <div className="text-center text-[#7A7873] py-12 text-sm">
              Cart is empty.
            </div>
          )}

          {cartArr.length > 0 && (
            <div className="pt-4 mt-2 border-t border-[#EBE8E2] space-y-2">
              <Input
                placeholder="Your name (optional)"
                value={customer.name}
                onChange={(e) =>
                  setCustomer({ ...customer, name: e.target.value })
                }
                data-testid="pub-name"
              />
              <Input
                placeholder="Phone (optional)"
                value={customer.phone}
                onChange={(e) =>
                  setCustomer({ ...customer, phone: e.target.value })
                }
              />
              <Input
                placeholder="Special instructions"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <p className="text-[11px] text-[#7A7873]">
                Final total may vary slightly due to taxes. Payment at counter /
                table after meal.
              </p>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[#EBE8E2] bg-[#FBFAF8]">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-[#7A7873]">Estimated Subtotal</span>
            <span className="font-mono-num font-bold text-lg text-[#2D2C2A]">
              {inr(subtotal)}
            </span>
          </div>
          <Button
            className="w-full h-11 bg-[#D95D39] hover:bg-[#C24F2C] text-white font-semibold"
            onClick={placeOrder}
            disabled={cartArr.length === 0 || submitting}
            data-testid="place-order-btn"
          >
            {submitting
              ? "Placing…"
              : `Place Order · ${totalQty} item${totalQty !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
